import { useCallback, useState } from "react";
import axios, { AxiosResponse } from "axios";
import toast from "react-hot-toast";
import { COUPON_SERVICE_APIS, GIFT_CONFIG_APIS } from "@/api_framework/api_config";
import {
  IGift,
  IGiftActionBody,
  IGiftFetchData,
  IGiftFetchResponse,
  IGiftFormatData,
  IGiftFormatResponse,
  IGiftListData,
  IGiftListResponse,
  IGiftMutationResponse,
  IGiftVariantNamesResponse,
} from "@/api_framework/api_modals/GiftConfig";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { buildAuthHeaders, readErrorMessage } from "../quick_commerce/homeTileApiUtils";

const extractGifts = (data: IGiftListData): IGift[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.gifts ?? data.results ?? data.data ?? [];
};

const extractGift = (data: IGiftFetchData): IGift | null => {
  if (!data) return null;
  return "gift_name" in data ? data : (data.gift ?? null);
};

const asStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.trim() !== "") : [];

const normalizeFormat = (data: IGiftFormatData | null): IGiftFormatData => ({
  allowed_statuses: asStringList(data?.allowed_statuses),
  allowed_gift_types: asStringList(data?.allowed_gift_types),
  allowed_operations: asStringList(data?.allowed_operations),
  allowed_platforms: asStringList(data?.allowed_platforms),
  allowed_discount_types: asStringList(data?.allowed_discount_types),
});

export const useGiftSetupAPI = () => {
  const configuration = useConfiguration();
  const [giftList, setGiftList] = useState<IGift[]>([]);
  const [isListLoading, setIsListLoading] = useState<boolean>(true);
  const [listError, setListError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [giftFormat, setGiftFormat] = useState<IGiftFormatData | null>(null);
  const [isFormatLoading, setIsFormatLoading] = useState<boolean>(false);

  const accessTokenKey = configuration.localStorage.ACCESS_TOKEN.key;

  const postAction = useCallback(
    <TResponse>(body: IGiftActionBody): Promise<AxiosResponse<TResponse>> =>
      axios.post<TResponse>(GIFT_CONFIG_APIS.ACTIONS, body, { headers: buildAuthHeaders(accessTokenKey) }),
    [accessTokenKey],
  );

  const fetchGiftList = useCallback(async (): Promise<IGift[] | null> => {
    setIsListLoading(true);
    try {
      const res = await postAction<IGiftListResponse>({ action: "list" });
      if (!res.data?.status) {
        setGiftList([]);
        setListError(res.data?.message || "Couldn't load gifts.");
        return null;
      }
      const gifts = extractGifts(res.data.data).filter((gift) => Boolean(gift.gift_id));
      setGiftList(gifts);
      setListError(null);
      return gifts;
    } catch (error) {
      setGiftList([]);
      setListError(readErrorMessage(error) || "Couldn't load gifts.");
      return null;
    } finally {
      setIsListLoading(false);
    }
  }, [postAction]);

  /** Loads the option values (statuses, gift types, operations, platforms) the backend accepts. */
  const fetchGiftFormat = useCallback(async (): Promise<IGiftFormatData | null> => {
    setIsFormatLoading(true);
    try {
      const res = await postAction<IGiftFormatResponse>({ action: "format" });
      if (!res.data?.status || !res.data.data) {
        toast.error(res.data?.message || "Couldn't load gift options.");
        return null;
      }
      const format = normalizeFormat(res.data.data);
      setGiftFormat(format);
      return format;
    } catch (error) {
      toast.error(readErrorMessage(error) || "Couldn't load gift options.");
      return null;
    } finally {
      setIsFormatLoading(false);
    }
  }, [postAction]);

  const fetchGift = useCallback(
    async (giftId: string): Promise<IGift | null> => {
      setIsFetching(true);
      try {
        const res = await postAction<IGiftFetchResponse>({ action: "fetch", gift_id: giftId });
        if (!res.data?.status) {
          toast.error(res.data?.message || "Couldn't load the gift.");
          return null;
        }
        return extractGift(res.data.data);
      } catch (error) {
        toast.error(readErrorMessage(error) || "Couldn't load the gift. Please try again.");
        return null;
      } finally {
        setIsFetching(false);
      }
    },
    [postAction],
  );

  const saveGift = useCallback(
    /** Inserts or updates the gift and resolves with the saved gift_id, or null when it failed. */
    async (gift: IGift, giftId?: string): Promise<string | null> => {
      setIsSaving(true);
      try {
        const body: IGiftActionBody = giftId
          ? { action: "update", gift_id: giftId, gift: { ...gift, gift_id: giftId } }
          : { action: "insert", gift: { ...gift, gift_id: null } };
        const res = await postAction<IGiftMutationResponse>(body);
        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to save the gift.");
          return null;
        }
        toast.success(res.data.message || (giftId ? "Gift updated." : "Gift created."));
        return res.data.data?.gift_id ?? giftId ?? "";
      } catch (error) {
        toast.error(readErrorMessage(error) || "Failed to save the gift. Please try again.");
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [postAction],
  );

  /**
   * Looks up product (variant) names by id. The gifts API stores product condition ids without names, so
   * this reuses the coupon service's variant-id validation, which takes a CSV of ids and returns their names.
   * Runs quietly: on any failure the chips simply keep showing ids.
   */
  const fetchVariantNames = useCallback(
    async (variantIds: string[]): Promise<Record<string, string>> => {
      const uniqueIds = Array.from(new Set(variantIds.filter(Boolean)));
      if (!uniqueIds.length) return {};
      try {
        const csv = ["variant_id", ...uniqueIds].join("\n");
        const formData = new FormData();
        formData.append("file", new File([csv], "gift_variant_ids.csv", { type: "text/csv" }));
        const res = await axios.post<IGiftVariantNamesResponse>(
          COUPON_SERVICE_APIS?.UPLOAD_VARIANTS?.baseURL ?? "",
          formData,
          // The stored token already includes "Bearer "; axios sets the multipart boundary itself.
          { headers: { Authorization: localStorage.getItem(accessTokenKey) ?? "" } },
        );
        const variants = res.data?.data?.valid_variants ?? [];
        return Object.fromEntries(
          variants
            .filter((variant) => variant?.vetic_variant_id && variant.sku_name)
            .map((variant) => [variant.vetic_variant_id, variant.sku_name]),
        );
      } catch {
        return {};
      }
    },
    [accessTokenKey],
  );

  const deleteGift = useCallback(
    async (giftId: string): Promise<boolean> => {
      setIsDeleting(true);
      try {
        const res = await postAction<IGiftMutationResponse>({ action: "delete", gift_id: giftId });
        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to delete the gift.");
          return false;
        }
        setGiftList((current) => current.filter((gift) => gift.gift_id !== giftId));
        toast.success("Gift deleted.");
        return true;
      } catch (error) {
        toast.error(readErrorMessage(error) || "Failed to delete the gift. Please try again.");
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [postAction],
  );

  return {
    giftList,
    isListLoading,
    listError,
    isFetching,
    isSaving,
    isDeleting,
    giftFormat,
    isFormatLoading,
    fetchGiftList,
    fetchGiftFormat,
    fetchVariantNames,
    fetchGift,
    saveGift,
    deleteGift,
  };
};
